import React from 'react';
import {useCallback, useMemo} from 'react';
import {I18nManager, TouchableOpacity, View} from 'react-native';
import {useSelector} from 'react-redux';
import styled from 'styled-components/native';
import dayjs from 'dayjs';
import Animated, {FadeInDown} from 'react-native-reanimated';
import TextGeneric from '../textGeneric';
import lightTheme, {Theme} from '../../theme/lightTheme';
import {getUrlParts, isUrl, openUrl, removeHtmlTags} from '../../utils/helpers';
import {RootState} from '../../redux/store';
import {NewsItemDetailsType} from '../../interfaces/api/tabs';
import {tr} from '../../translations';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import {isWeb} from '../../utils/responsive';
import {renderArticleContent} from '../../utils/html';
import AutoHeightWebView from '../autoHeightWebView';
import {useTheme} from 'styled-components';

// Types
interface TableItemViewProps {
  title: string;
  value: string;
  isReadMoreText?: boolean;
  isWebview?: boolean;
  index: number;
}

interface UrlPartsProps {
  value?: string;
  isLanguageRTL?: boolean;
  font?: any;
  isReadMoreText?: boolean;
  isWebview?: boolean;
  dynamicWidth?: boolean;
  theme: Theme;
  wdp: (percentage: number) => number;
  onCustomSeeMorePress?: () => void;
  viewMoreLabel?: string;
}

// Styled Components
const Container = styled.View`
  width: 100%;
`;

const AnimatedTabItem = Animated.createAnimatedComponent(styled.View<{
  isLanguageRTL?: boolean;
  responsivePadding?: string;
}>`
  width: 100%;
  flex-direction: ${({isLanguageRTL}) =>
    isLanguageRTL && !I18nManager.isRTL ? 'row-reverse' : 'row'};
  border-radius: 16px;
  padding: ${({responsivePadding}) => responsivePadding};
  background-color: ${({theme}) => theme.cardBackground};
  margin-bottom: ${({theme}) => theme.text.s12}px;
  align-items: flex-start;
  justify-content: space-between;
  overflow: hidden;
`);

const TitleText = styled(TextGeneric)<{fontSize: number; lineHeight: number}>`
  margin-right: 10px;
  color: ${({theme}) => theme.text.dark};
  font-size: ${({fontSize}) => fontSize}px;
  line-height: ${({lineHeight}) => lineHeight}px;
`;

const LinkText = styled(TextGeneric)<{
  fontSize: number;
  lineHeight: number;
  isRTL: boolean;
  dynamicWidth?: boolean;
  width: number;
}>`
  flex: 1;
  color: ${({theme}) => theme.text.dark};
  text-decoration-line: underline;
  width: ${({dynamicWidth, width}) => (dynamicWidth ? '100%' : `${width}px`)};
  align-self: ${({isRTL}) =>
    isRTL && !I18nManager.isRTL ? 'flex-start' : 'flex-end'};
  font-size: ${({fontSize}) => fontSize}px;
  line-height: ${({lineHeight}) => lineHeight}px;
`;

const ContentText = styled(TextGeneric)<{
  fontSize: number;
  lineHeight: number;
  isLanguageRTL?: boolean;
}>`
  flex: 1;
  color: ${({theme}) => theme.text.dark};
  font-size: ${({fontSize}) => fontSize}px;
  line-height: ${({lineHeight}) => lineHeight}px;
  text-align: ${({isLanguageRTL}) => (isLanguageRTL ? 'right' : 'left')};
`;

const MoreText = styled(TextGeneric)<{fontSize: number; lineHeight: number}>`
  color: ${({theme}) => theme.primary};
  font-size: ${({fontSize}) => fontSize}px;
  line-height: ${({lineHeight}) => lineHeight}px;
`;

const ContentView = styled.View`
  flex: 1;
`;

/**
 * TableItem Component - Displays structured data in a table format with animations
 */
const TableItem = (props: NewsItemDetailsType) => {
  const {
    specialty,
    subspecialty,
    url,
    publisher,
    resource,
    readabilityscore,
    sentiment,
    accesslevel,
    publishdate,
    type_of_study_value,
    description,
    origin,
    summary,
    summarydate,
    onCustomSeeMorePress,
    isLanguageRTL,
    tableTranslations,
  } = props;

  // Hooks
  const userStatus = useSelector((state: RootState) => state?.auth);
  const {font} = userStatus?.config || {};
  const {wdp, isMobile, getByScreenSize} = useResponsiveDimensions();

  // Calculate font sizes once
  const fontSize = font?.fontSize || lightTheme.text.s8;
  const lineHeight = fontSize * 1.4;

  /**
   * Renders URL parts with clickable links
   */
  const renderUrlParts = useCallback(
    (params: Partial<UrlPartsProps>) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const theme = useTheme() as Theme;
      const {value, isReadMoreText, isWebview, dynamicWidth} = params;
      const parts = getUrlParts((value || '').replace(/http/g, '  http') || '');

      if (isWebview) {
        return (
          <View style={{flex: 1}}>
            <AutoHeightWebView
              style={{width: '100%', flex: 1}}
              source={{
                html: renderArticleContent(value || '', {
                  theme,
                  isArticle: true,
                  isRTL: isLanguageRTL,
                  fontSize: font?.fontSize,
                }),
              }}
              scrollEnabled={false}
              onShouldStartLoadWithRequest={(event: any) => {
                if (isUrl(event?.url)) {
                  openUrl(event.url);
                  return false;
                }
                return true;
              }}
              injectedJavaScript="window.ReactNativeWebView.postMessage(document.body.scrollHeight)"
            />
          </View>
        );
      }

      return (
        <View style={{flex: 1}}>
          {parts.map((part: string, index: number) => {
            if (!part || part.trim().length === 0) {
              return null;
            }

            if (isUrl(part)) {
              return (
                <TouchableOpacity
                  key={index}
                  style={{
                    width: !dynamicWidth ? wdp(40) : undefined,
                    flex: 1,
                  }}
                  onPress={() => openUrl(part)}>
                  <LinkText
                    bold={font?.fontWeight === 'bold'}
                    fontSize={fontSize}
                    lineHeight={lineHeight}
                    isRTL={!!isLanguageRTL}
                    dynamicWidth={dynamicWidth}
                    width={wdp(40)}>
                    {part}
                  </LinkText>
                </TouchableOpacity>
              );
            }

            return (
              <ContentText
                key={index}
                numberOfLines={
                  !isReadMoreText || (isWeb && isMobile) ? undefined : 3
                }
                bold={font?.fontWeight === 'bold'}
                fontSize={fontSize}
                lineHeight={lineHeight}
                isLanguageRTL={isLanguageRTL}>
                {isWeb && isMobile
                  ? (isReadMoreText ? removeHtmlTags(part) : part).slice(
                      0,
                      20,
                    ) +
                    ((isReadMoreText ? removeHtmlTags(part) : part).length > 20
                      ? ' ...'
                      : '')
                  : isReadMoreText
                    ? removeHtmlTags(part)
                    : part}
              </ContentText>
            );
          })}
        </View>
      );
    },
    [font, isLanguageRTL, wdp, fontSize, lineHeight],
  );

  /**
   * Renders a single table row
   */
  const TableItemView = useCallback(
    ({title, value, isReadMoreText, isWebview, index}: TableItemViewProps) => {
      if (!value) return null;
      return (
        <AnimatedTabItem
          responsivePadding={getByScreenSize('5%', '2%')}
          entering={FadeInDown.delay(index * 100).springify()}
          isLanguageRTL={isLanguageRTL}>
          <TitleText
            bold
            fontSize={fontSize}
            lineHeight={lineHeight}
            style={{
              width: wdp(getByScreenSize(40, 15)),
              textAlign: isLanguageRTL ? 'right' : 'left',
            }}>
            {(title || '').replace('-', '/\n')}
          </TitleText>

          {isReadMoreText && (value || '').length > 20 ? (
            <ContentView>
              {renderUrlParts({
                value,
                isReadMoreText,
                isLanguageRTL,
                isWebview,
              })}
              <TouchableOpacity onPress={onCustomSeeMorePress}>
                <MoreText bold fontSize={fontSize} lineHeight={lineHeight}>
                  {tableTranslations?.viewMore || tr('readMoreText.more')}
                </MoreText>
              </TouchableOpacity>
            </ContentView>
          ) : (
            renderUrlParts({value, isLanguageRTL, isWebview})
          )}
        </AnimatedTabItem>
      );
    },
    [
      fontSize,
      lineHeight,
      wdp,
      isLanguageRTL,
      onCustomSeeMorePress,
      renderUrlParts,
      tableTranslations,
    ],
  );

  // Data mapping with proper filtering
  const detailsMap = useMemo(
    () =>
      [
        {
          title: (tableTranslations?.specialty || 'Specialty') + ':',
          value: specialty,
        },
        {
          title: (tableTranslations?.subspecialty || 'Topic') + ':',
          value: subspecialty,
        },
        {
          title: (tableTranslations?.url || 'Link / Reference') + ':',
          value: url,
        },
        {
          title: (tableTranslations?.publisher || 'Publisher') + ':',
          value: publisher,
        },
        {
          title: (tableTranslations?.resource || 'Resource') + ':',
          value: resource,
        },
        {title: (tableTranslations?.origin || 'Origin') + ':', value: origin},
        readabilityscore && {
          title:
            (tableTranslations?.readabilityscore || 'Readability score') + ':',
          value: parseFloat(readabilityscore).toFixed(2),
        },
        {
          title: (tableTranslations?.sentiment || 'Sensation') + ':',
          value: sentiment,
        },
        {
          title: (tableTranslations?.accesslevel || 'Access Level') + ':',
          value: accesslevel,
        },
        (publishdate || summarydate) && {
          title: (tableTranslations?.date || 'Date') + ':',
          value: dayjs(publishdate || summarydate).format('YYYY'),
        },
        {
          title:
            (tableTranslations?.type_of_study_value || 'Type of the study') +
            ':',
          value: type_of_study_value,
        },
        {
          title: (tableTranslations?.summary || 'Summary') + ':',
          value: isMobile
            ? removeHtmlTags(description || summary || '')
            : description || summary || '',
          isReadMoreText: isMobile,
          isWebview: !isMobile,
        },
      ].filter(Boolean),
    [
      specialty,
      subspecialty,
      url,
      publisher,
      resource,
      readabilityscore,
      sentiment,
      accesslevel,
      publishdate,
      type_of_study_value,
      description,
      origin,
      summary,
      summarydate,
      tableTranslations,
      isMobile,
    ],
  );

  return (
    <Container>
      {detailsMap.map((item: any, index) => (
        <TableItemView
          key={index}
          index={index}
          title={item.title}
          value={item.value}
          isReadMoreText={item.isReadMoreText}
          isWebview={item.isWebview}
        />
      ))}
    </Container>
  );
};

export default TableItem;
